<template>
  <TransitionRoot as="template" :show="open">
    <Dialog as="div" class="relative z-50" @close="$emit('close')">
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
      </TransitionChild>

      <div class="fixed inset-0 z-10 overflow-y-auto">
        <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-3xl">
              <div class="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                <div class="sm:flex sm:items-start">
                  <div class="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
                    <DialogTitle as="h3" class="text-xl font-semibold leading-6 text-gray-900">
                      Integration Script for {{ course.title }}
                    </DialogTitle>
                    
                    <div class="mt-4">
                      <!-- Course Info -->
                      <div class="mb-4 p-3 bg-gray-50 rounded-lg">
                        <div class="grid grid-cols-2 gap-2 text-sm">
                          <div>
                            <span class="font-medium text-gray-700">Course ID:</span>
                            <span class="ml-2 text-gray-900">{{ course.course_id }}</span>
                          </div>
                          <div>
                            <span class="font-medium text-gray-700">Method:</span>
                            <span class="ml-2 text-gray-900">{{ formatMethod(course.metadata?.integration_method || course.integration_method || 'manual') }}</span>
                          </div>
                        </div>
                      </div>
                      
                      <!-- Manual Script Section -->
                      <div v-if="(course.metadata?.integration_method || course.integration_method) === 'manual' || (course.metadata?.integration_method || course.integration_method) === 'copy_paste' || !(course.metadata?.integration_method || course.integration_method)">
                        <div class="mb-4">
                          <h4 class="font-medium text-sm mb-2">Your Integration Script:</h4>
                          
                          <!-- Tool Type Tabs (if known) -->
                          <div v-if="course.metadata?.tool_type || course.tool_type || course.platform" class="mb-3">
                            <div class="flex space-x-2">
                              <button
                                @click="scriptType = 'javascript'"
                                :class="[
                                  'px-3 py-1 text-sm rounded',
                                  scriptType === 'javascript' 
                                    ? 'bg-teal-600 text-white' 
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                ]"
                              >
                                JS Trigger (Storyline/Captivate)
                              </button>
                              <button
                                @click="scriptType = 'html'"
                                :class="[
                                  'px-3 py-1 text-sm rounded',
                                  scriptType === 'html' 
                                    ? 'bg-teal-600 text-white' 
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                ]"
                              >
                                HTML/LMS Script
                              </button>
                            </div>
                          </div>
                          
                          <!-- Script Display -->
                          <pre class="bg-gray-900 text-green-400 p-4 rounded text-xs overflow-x-auto">{{ getScript() }}</pre>
                          
                          <button 
                            @click="copyScript"
                            class="mt-3 bg-teal-600 text-white px-4 py-2 rounded hover:bg-teal-700 flex items-center space-x-2"
                          >
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                            </svg>
                            <span>{{ copied ? 'Copied!' : 'Copy Script' }}</span>
                          </button>
                        </div>
                        
                        <!-- Instructions -->
                        <div class="p-4 bg-blue-50 rounded-lg">
                          <h4 class="font-medium text-sm mb-2 text-blue-900">Installation Instructions:</h4>
                          
                          <!-- JavaScript Trigger Instructions -->
                          <div v-if="scriptType === 'javascript'" class="text-sm text-blue-800">
                            <p class="mb-2 font-medium">For JavaScript Triggers (no &lt;script&gt; tags needed):</p>
                            <div v-if="course.tool_type === 'storyline' || !course.tool_type" class="mb-3">
                              <p class="font-medium text-xs uppercase text-blue-700 mb-1">Storyline:</p>
                            <ol class="list-decimal list-inside space-y-1">
                              <li>Open your project in Storyline</li>
                              <li>View → Slide Master</li>
                              <li>Select the topmost master slide</li>
                              <li>Insert → Trigger → Execute JavaScript</li>
                              <li>When: Timeline starts</li>
                              <li>Paste the JavaScript code (without &lt;script&gt; tags) and click OK</li>
                            </ol>
                            </div>
                            <div v-if="course.tool_type === 'captivate' || !course.tool_type" class="mb-3">
                              <p class="font-medium text-xs uppercase text-blue-700 mb-1">Captivate:</p>
                            <ol class="list-decimal list-inside space-y-1">
                              <li>Open your project in Captivate</li>
                              <li>Select the first slide</li>
                              <li>Window → Actions</li>
                              <li>On Enter → Execute JavaScript</li>
                              <li>Paste the JavaScript code (without &lt;script&gt; tags) and click OK</li>
                            </ol>
                            </div>
                          </div>
                          
                          <!-- HTML Script Instructions -->
                          <div v-else class="text-sm text-blue-800">
                            <p class="mb-2 font-medium">For HTML/LMS Integration (includes &lt;script&gt; tags):</p>
                            <ul class="list-disc list-inside space-y-1">
                              <li>Direct HTML editing: Add to the &lt;head&gt; or &lt;body&gt; section</li>
                              <li>LMS custom scripts: Paste in the custom JavaScript/HTML field</li>
                              <li>SCORM package: Edit index.html or story.html file</li>
                              <li>Web page: Add before the closing &lt;/body&gt; tag</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                      
                      <!-- Upload Method Section -->
                      <div v-else-if="(course.metadata?.integration_method || course.integration_method) === 'upload' || (course.metadata?.integration_method || course.integration_method) === 'package_upload'">
                        <div class="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                          <div class="flex items-start">
                            <svg class="w-5 h-5 text-amber-600 mr-3 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
                            </svg>
                            <div class="flex-1">
                              <h4 class="font-medium text-amber-900 mb-2">Package Processing Method</h4>
                              <p class="text-sm text-amber-800 mb-3">
                                This course was integrated by processing a SCORM/xAPI package locally in your browser. The Happy Alien script was automatically injected into the package files.
                              </p>
                              
                              <!-- Check for saved package -->
                              <div v-if="savedPackage" class="mb-3 p-3 bg-white rounded border border-amber-200">
                                <div class="flex items-center justify-between">
                                  <div class="text-sm">
                                    <div class="font-medium text-gray-900">Previous package available</div>
                                    <div class="text-gray-500">Processed {{ formatTimeAgo(savedPackage.timestamp) }}</div>
                                  </div>
                                  <button
                                    @click="downloadSavedPackage"
                                    class="px-3 py-1 bg-amber-600 text-white text-sm rounded hover:bg-amber-700"
                                  >
                                    Download
                                  </button>
                                </div>
                              </div>
                              
                              <div class="space-y-2">
                                <p class="text-sm font-medium text-amber-900">Need to update your course?</p>
                                <ol class="list-decimal list-inside text-sm text-amber-800 space-y-1">
                                  <li>Make your changes in your authoring tool</li>
                                  <li>Export a new SCORM/xAPI package</li>
                                  <li>Click the button below to re-process it locally</li>
                                </ol>
                                <button
                                  @click="openReprocessModal"
                                  class="mt-3 bg-amber-600 text-white px-4 py-2 rounded hover:bg-amber-700 flex items-center space-x-2"
                                >
                                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"></path>
                                  </svg>
                                  <span>Process Updated Package</span>
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <!-- Show the script that gets injected with tabs -->
                        <div class="mt-4">
                          <h4 class="font-medium text-sm mb-2">Script Automatically Injected (locally in your browser):</h4>
                          
                          <!-- Script Type Tabs -->
                          <div class="mb-3">
                            <div class="flex space-x-2">
                              <button
                                @click="scriptType = 'javascript'"
                                :class="[
                                  'px-3 py-1 text-sm rounded',
                                  scriptType === 'javascript' 
                                    ? 'bg-teal-600 text-white' 
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                ]"
                              >
                                JS Trigger (Storyline/Captivate)
                              </button>
                              <button
                                @click="scriptType = 'html'"
                                :class="[
                                  'px-3 py-1 text-sm rounded',
                                  scriptType === 'html' 
                                    ? 'bg-teal-600 text-white' 
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                ]"
                              >
                                HTML/LMS Script
                              </button>
                            </div>
                          </div>
                          
                          <pre class="bg-gray-900 text-green-400 p-4 rounded text-xs overflow-x-auto">{{ getScript() }}</pre>
                          
                          <button 
                            @click="copyScript"
                            class="mt-3 bg-teal-600 text-white px-4 py-2 rounded hover:bg-teal-700 flex items-center space-x-2"
                          >
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                            </svg>
                            <span>{{ copied ? 'Copied!' : 'Copy Script' }}</span>
                          </button>
                        </div>
                      </div>
                      
                      <!-- Fallback: Show script for any other/unknown method -->
                      <div v-else>
                        <div class="mb-4">
                          <h4 class="font-medium text-sm mb-2">Your Integration Script:</h4>
                          <p class="text-xs text-gray-600 mb-3">Add this script to your course to enable Happy Alien features.</p>
                          
                          <!-- Script Display -->
                          <pre class="bg-gray-900 text-green-400 p-4 rounded text-xs overflow-x-auto">{{ getHtmlScript() }}</pre>
                          
                          <!-- Copy Button -->
                          <div class="mt-3 flex justify-between items-center">
                            <button
                              @click="copyScript"
                              class="px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700 text-sm"
                            >
                              {{ copied ? 'Copied!' : 'Copy Script' }}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div class="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                <button
                  @click="$emit('close')"
                  type="button"
                  class="inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:w-auto"
                >
                  Close
                </button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue'

interface Course {
  id: string
  course_id: string
  title: string
  tool_type?: string
  integration_method: string
  created_at: string
}

const props = defineProps<{
  open: boolean
  course: Course
  orgId: string
}>()

const emit = defineEmits<{
  close: []
  reprocess: [course: Course]
}>()

// State
const scriptType = ref('html')  // Default to HTML version
const copied = ref(false)
const savedPackage = ref<any>(null)

// Computed
const isJavaScriptTool = computed(() => {
  const toolType = props.course.metadata?.tool_type || props.course.tool_type || props.course.platform || ''
  return ['storyline', 'captivate', 'lectora'].includes(toolType)
})

// Methods
function getScript(): string {
  if (scriptType.value === 'javascript') {
    return getJavaScriptCode()
  }
  return getHtmlScript()
}

function getJavaScriptCode(): string {
  return `(function() {
  // Happy Alien Universal Loader
  // Course: ${props.course.title}
  // ID: ${props.course.course_id}
  
  // Prevent multiple loads (important for Slide Masters)
  if (window.happyAlienLoaded) return;
  window.happyAlienLoaded = true;
  
  var script = document.createElement('script');
  script.id = 'happyalien-loader';
  script.setAttribute('data-org', '${props.orgId}');
  script.setAttribute('data-course', '${props.course.course_id}');
  script.src = 'https://api.happyalien.ai/loader.js';
  
  // Add load confirmation
  script.onload = function() {
    console.log('[HappyAlien] Loaded for: ${props.course.title}');
  };
  
  document.head.appendChild(script);
})();`
}

function getHtmlScript(): string {
  const scriptOpen = '<script'
  const scriptClose = '<' + '/script>'
  return `<!-- Happy Alien Universal Loader -->
<!-- Course: ${props.course.title} -->
${scriptOpen} id="happyalien-loader" 
  data-org="${props.orgId}" 
  data-course="${props.course.course_id}"
  src="https://api.happyalien.ai/loader.js">
${scriptClose}`
}

async function copyScript() {
  const script = getScript()
  
  try {
    await navigator.clipboard.writeText(script)
    copied.value = true
    setTimeout(() => {
      copied.value = false
    }, 2000)
  } catch (err) {
    alert('Failed to copy script. Please select and copy manually.')
  }
}

function formatMethod(method: string): string {
  const methods: { [key: string]: string } = {
    'manual': 'Manual Script',
    'copy_paste': 'Copy/Paste',
    'upload': 'Package Upload',
    'package_upload': 'SCORM Upload'
  }
  return methods[method] || method
}

function formatTimeAgo(timestamp: number): string {
  const seconds = Math.floor((Date.now() - timestamp) / 1000)
  
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`
  const days = Math.floor(hours / 24)
  return `${days} day${days !== 1 ? 's' : ''} ago`
}

function checkForSavedPackage() {
  // Check if there's a saved package for this course
  const packages = localStorage.getItem('echocast_processed_packages')
  if (packages) {
    const allPackages = JSON.parse(packages)
    savedPackage.value = allPackages.find((pkg: any) => 
      pkg.courseId === props.course.course_id &&
      Date.now() - pkg.timestamp < 24 * 60 * 60 * 1000
    )
  }
}

async function downloadSavedPackage() {
  if (!savedPackage.value) return
  
  try {
    // Convert base64 back to blob
    const response = await fetch(savedPackage.value.blob)
    const blob = await response.blob()
    
    // Create download link
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${savedPackage.value.courseId}-happy-alien-enabled.zip`
    a.click()
    URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Failed to download saved package:', error)
    alert('Failed to download package. It may have expired.')
  }
}

function openReprocessModal() {
  emit('reprocess', props.course)
  emit('close')
}

// Lifecycle
onMounted(() => {
  // Set default script type based on tool
  if (isJavaScriptTool.value) {
    scriptType.value = 'javascript'
  } else {
    scriptType.value = 'html'
  }
  
  // Check for saved package if it was uploaded
  if (props.course.integration_method === 'upload' || props.course.integration_method === 'package_upload') {
    checkForSavedPackage()
  }
})
</script>